import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import { isEqual, createUrlParameters } from '@comeon/utility';
import { LOGOUT, AUTHENTICATION_COMPLETE, LOGIN_START } from '@comeon/actions-public';
import Cookies from '@comeon/tomcat-cookies';
import reducerRegistry from '@comeon/reducer-registry';
import {
	getAccount,
	getBalance,
	getPoints,
	getCurrency,
	getPlayerTotalDeposit,
	getNumOfActiveOffers,
	getSowfiInfo,
	getRiskLevels,
	getRemainingBonusValue,
	getSelfExclusionValue,
	getPlayerAtLeastOneFinalizedInterviewExists
} from '../index.js';
import { postJson, postFormUrlEncoded } from '~/core/ajax-json.js';
import {
	COOKIE_PLAYER_POINTS,
	COOKIE_PLAYER_BALANCE,
	COOKIE_PLAYER_CASINO_NET_POSITION,
	setCookie
} from '~/store/cookies.js';
import { requestApi } from '~/services/api.js';
import { showNotification } from '~/components/notifications/ducks/notifications.js';
import { sendAppBridgeEvent, WEB_EVENTS } from '~/components/app-bridge';
import { OPERATION_TYPES } from '~/components/payment/suspended-players-modal/constants';

const REDUCER_NAME = 'account';
const NL_AGE_LIMIT = 24;

/** *************************************************************************
 * ACTIONS
 ************************************************************************** */

const ACCOUNT_HYDRATE = '@komigen/mobile-frontend/account/ACCOUNT_HYDRATE';
const ACCOUNT_UPDATE = '@komigen/mobile-frontend/account/ACCOUNT_UPDATE';
const REMAINING_GERMANY_BONUS_VALUE_UPDATE = '@komigen/mobile-frontend/account/REMAINING_GERMANY_BONUS_VALUE_UPDATE';
const ACCOUNT_GETRISK = '@komigen/mobile-frontend/account/ACCOUNT_GETRISK';
const ACCOUNT_SET_BALANCE = '@komigen/mobile-frontend/account/SET_BALANCE';
const SAVE_CURRENCY = '@komigen/mobile-frontend/account/SAVE_CURRENCY';
const CURRENCY_LIMITS = '@komigen/mobile-frontend/account/CURRENCY_LIMITS';
const SAVE_AMOUNT = '@komigen/mobile-frontend/account/SELECT_AMOUNT';
const CACHE_REFERRER = '@komigen/mobile-frontend/CACHE_REFERRER';
const SET_REASON_GAMBLING = '@komigen/mobile-frontend/SET_REASON_GAMBLING';
const SET_FROM_RG = '@komigen/mobile-frontend/SET_FROM_RG';
const SET_CLOSING_REASON = '@komigen/mobile-frontend/SET_CLOSING_REASON';
const SET_OASIS_REASON = '@komigen/mobile-frontend/SET_OASIS_REASON';
const SET_SHOW_CLOSED_POPUP = '@komigen/mobile-frontend/SET_SHOW_CLOSED_POPUP';
const SET_CASINO_NET_POSITION = '@komigen/mobile-frontend/account/SET_CASINO_NET_POSITION';
const SET_NUM_OF_ACTIVE_OFFERS = '@komigen/mobile-frontend/account/SET_NUM_OF_ACTIVE_OFFERS';
const SET_PLAYER_ID = '@komigen/mobile-frontend/account/SET_PLAYER_ID';
const UPDATE_VERIFICATION_MOBILE_STATUS = '@komigen/mobile-frontend/UPDATE_VERIFICATION_MOBILE_STATUS';
const HIDE_MENTOR_MESSAGE_DIALOG = '@komigen/mobile-frontend/authentication/HIDE_MENTOR_MESSAGE_DIALOG';
const SHOW_MENTOR_MESSAGE_DIALOG = '@komigen/mobile-frontend/authentication/SHOW_MENTOR_MESSAGE_DIALOG';
const UPDATE_MENTOR_MESSAGES = '@komigen/mobile-frontend/authentication/UPDATE_MENTOR_MESSAGES';
const DEPOSIT_RESULT_PAGE_OPEN = '@komigen/mobile-frontend/authentication/DEPOSIT_RESULT_PAGE_OPEN';
const ACCOUNT_VERIFICATIONS_UPDATE = '@komigen/mobile-frontend/authentication/ACCOUNT_VERIFICATIONS_UPDATE';
const TWO_FACTOR_AUTH_REQUEST_SMS = '@komigen/mobile-frontend/authentication/TWO_FACTOR_AUTH_REQUEST_SMS';
const TWO_FACTOR_AUTH_ENABLED = '@komigen/mobile-frontend/authentication/TWO_FACTOR_AUTH_ENABLED';
const TWO_FACTOR_AUTH_DISABLED = '@komigen/mobile-frontend/authentication/TWO_FACTOR_AUTH_DISABLED';
const TWO_FACTOR_AUTH_STATUS_RESET = '@komigen/mobile-frontend/authentication/TWO_FACTOR_AUTH_STATUS_RESET';
const TWO_FACTOR_AUTH_REQUEST_SMS_RESET = '@komigen/mobile-frontend/authentication/TWO_FACTOR_AUTH_REQUEST_SMS_RESET';
const PLAYER_SELF_EXCLUDED_UPDATE = '@komigen/mobile-frontend/account/PLAYER_SELF_EXCLUDED_UPDATE';
const PLAYER_SELF_EXCLUDED_FAILURE = '@komigen/mobile-frontend/account/PLAYER_SELF_EXCLUDED_FAILURE';
const SET_SHOULD_SHOW_CROSS_MARKETING_CONSENT =
	'@komigen/mobile-frontend/account/SET_SHOULD_SHOW_CROSS_MARKETING_CONSENT';
const UPDATE_LIMITS_MODAL = '@komigen/mobile-frontend/account/UPDATE_LIMITS_MODAL';
const UPDATE_SUSPENDED_PLAYER_MODAL = '@komigen/mobile-frontend/account/UPDATE_SUSPENDED_PLAYER_MODAL';
const SET_CLOSING_ACCOUNT_STATUS = '@komigen/mobile-frontend/account/SET_CLOSING_ACCOUNT_STATUS';
const SET_LUGAS_ACTIVATION_TIME = '@komigen/mobile-frontend/account/SET_LUGAS_ACTIVATION_TIME';
/** *************************************************************************
 * SELECTORS
 ************************************************************************** */

const selectAccount = state => state.account;
const selectIsCasinoAndSportsBookProduct = state => state[REDUCER_NAME].isCasinoAndSportsBookProduct;
const selectBalance = state => state[REDUCER_NAME].balance;
const selectCasinoBalance = state => state[REDUCER_NAME]?.balancePerCategory?.CASINO ?? 0;
const selectSportsbookBalance = state => state[REDUCER_NAME]?.balancePerCategory?.SPORTSBOOK ?? 0;
const selectOddsDuelBalance = state => state[REDUCER_NAME]?.balancePerCategory?.ODDSDUEL ?? 0;
const selectPokerBalance = state => state[REDUCER_NAME]?.balancePerCategory?.POKER ?? 0;
const selectWespinBalance = state => state[REDUCER_NAME]?.balancePerCategory?.WESPIN ?? 0;
const selectAllBalances = createSelector(
	selectBalance,
	selectCasinoBalance,
	selectSportsbookBalance,
	selectOddsDuelBalance,
	selectPokerBalance,
	selectWespinBalance,
	(totalBalance, casinoBalance, sportsbookBalance, oddsDuelBalance, pokerBalance, wespinBalance) => ({
		totalBalance,
		casinoBalance,
		sportsbookBalance,
		oddsDuelBalance,
		pokerBalance,
		wespinBalance
	})
);
const selectCurrency = state => state[REDUCER_NAME].currency;
const selectLocale = state => state[REDUCER_NAME].locale;
const selectTestPlayer = state => state[REDUCER_NAME].testPlayer;
const selectFranchiseId = state => state[REDUCER_NAME].franchiseId;
const selectFormattedBalance = state => state[REDUCER_NAME].formattedBalance;
const selectPlayerId = state => state[REDUCER_NAME].id;
const selectPlayerToken = state => state[REDUCER_NAME].playerToken;
const selectCountryId = state => state[REDUCER_NAME].countryId;
const selectPoints = state => state[REDUCER_NAME].points;
const selectHydrated = state => state[REDUCER_NAME].hydrated || false;
const selectShowMentorMessageDialog = state => state[REDUCER_NAME].showMentorMessageDialog;
const selectExitPath = state => state[REDUCER_NAME].exitPath;
const selectMentorMessages = state => state[REDUCER_NAME].mentorMessages || [];
const selectIsReasonGambling = state => state[REDUCER_NAME].isReasonGambling;
const selectIsFromRG = state => state[REDUCER_NAME].isFromRG;
const selectClosingReason = state => state[REDUCER_NAME].closingReason;
const selectOasisReason = state => state[REDUCER_NAME].oasisReason;
const selectShowClosedPopup = state => state[REDUCER_NAME].showClosedPopup;
const selectLimitsData = state => state[REDUCER_NAME].playerRestoreDepositLimitVO;
const selectIsRegistration = state => state[REDUCER_NAME].isRegistration;
const selectCasinoNetPosition = state => state[REDUCER_NAME].casinoNetPosition;
const selectNumOfActiveOffers = state => state[REDUCER_NAME].numOfActiveOffers;
const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);
const accountDataSelector = createDeepEqualSelector(selectAccount, account => {
	return account;
});
const selectAutoWithdraw = state => state[REDUCER_NAME].autoWithdraw;

const selectPlayerFullName = state => {
	const { firstName, lastName } = state.account;
	return `${firstName} ${lastName}`;
};
const twoFactorAuthEnabled = state => state[REDUCER_NAME].twoFactorAuthenticationEnabled;
const selectPlayerVerifications = state => state[REDUCER_NAME].playerVerificationsVO;
const twoFactorAuthDisabled = state => state[REDUCER_NAME].twoFactorAuthenticationDisabled;
export const isTwoFactorAuthEnabled = state => state[REDUCER_NAME].mfaEnabled;
export const twoFactorAuthSMSSent = state => state[REDUCER_NAME].twoFactorAuthenticationSMSSent;
const selectInvertedPlayerFullName = state => {
	const { firstName, lastName } = state.account;
	return `${lastName} ${firstName}`;
};
const selectClosureDetails = state => ({
	accountReopenDate: state[REDUCER_NAME].accountReopenDate,
	text: state[REDUCER_NAME].text,
	code: state[REDUCER_NAME].code,
	reasonType: state[REDUCER_NAME].reasonType
});
export const selectDepositResultPageOpen = state => state[REDUCER_NAME].depositResultPageOpen;
const selectUsername = state => state[REDUCER_NAME].username;
const selectRemainingGermanyBonusValue = state => state[REDUCER_NAME].remainingGermanyBonusValue;
const selectDateOfBirth = state => state[REDUCER_NAME].birth;
const selectPlayerSelfExcluded = state => state[REDUCER_NAME].playerSelfExcluded;
const selectPlayerSelfExcludedFailure = state => state[REDUCER_NAME].playerSelfExcludedFailure;
const selectPlayerBonusBlocked = state => state[REDUCER_NAME].playerBonusBlocked;
const selectShouldShowSplashtech = state => state[REDUCER_NAME].shouldShowSplashtech;
const selectHasAutofilledAddress = state => state[REDUCER_NAME].hasAutofilledAddress; // selector state available just during registration.
const selectIntRef = state => state[REDUCER_NAME].intRef;

const selectIsAboveAgeLimit = state => {
	const { birth } = state.account;
	const { authenticated } = state.authentication;
	const { isNetherlandsRegulatedFranchise } = state.brand;

	if (!isNetherlandsRegulatedFranchise || !authenticated || !birth) return true;

	const today = new Date();
	const birthDate = new Date(birth.split('T')[0]);
	let age = today.getFullYear() - birthDate.getFullYear();
	const m = today.getMonth() - birthDate.getMonth();
	if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
		age -= 1;
	}
	return NL_AGE_LIMIT <= age;
};

export const selectShouldShowCrossMarketingConsent = state => state[REDUCER_NAME].shouldShowCrossMarketingConsent;
export const selectIsBonusAbuserPlayer = state => state[REDUCER_NAME].isBonusAbuser;
export const selectIsDepositBlocked = state => state[REDUCER_NAME].isDepositBlocked;
export const selectIsWithdrawBlocked = state => state[REDUCER_NAME].isWithdrawBlocked;
export const selectSuspendTimestamp = state => state[REDUCER_NAME].suspendTimestamp;

// Limits Modal
export const selectIsLimitsModalOpen = state => state[REDUCER_NAME].limitsModal?.open ?? false;
export const selectLimitsModalData = createSelector(
	state => state[REDUCER_NAME].limitsModal?.data,
	(data = {}) => data
);
export const selectCurrencyId = state => state[REDUCER_NAME].currencyId;

export const selectMobileCountryCode = state => state[REDUCER_NAME].mobileCountryCode;
export const selectMobile = state => state[REDUCER_NAME].mobile;
export const selectTemporaryPlayer = state => state[REDUCER_NAME].temporaryPlayer;
export const selectVerificationMessageSent = state => state[REDUCER_NAME].verificationMessageSent;
export const selectVerifiedMobileNumber = state => state[REDUCER_NAME].verifiedMobileNumber;
export const selectVerifiedMobileCountryCode = state => state[REDUCER_NAME].verifiedMobileCountryCode;
export const selectVerificationSMSLastSent = state => state[REDUCER_NAME].verificationSMSLastSent;

// Suspended players modal
export const selectIsSuspendedPlayerModalOpen = state => state[REDUCER_NAME].suspendedPlayerModal?.open ?? false;
export const selectSuspendedPlayerModalData = state => state[REDUCER_NAME].suspendedPlayerModal?.data ?? {};

export const selectClosingAccountStatus = state => state[REDUCER_NAME].isClosingAccount;
export const selectLugasActivationTime = state => state[REDUCER_NAME].lugasActivationTime;

/** *************************************************************************
 * ACTION CREATORS
 ************************************************************************** */

function playerVerificationsUpdate(payload) {
	return {
		type: ACCOUNT_VERIFICATIONS_UPDATE,
		payload
	};
}

function accountUpdate(payload) {
	return {
		type: ACCOUNT_UPDATE,
		payload
	};
}

function remainingGermanyBonusValueUpdate(payload) {
	return {
		type: REMAINING_GERMANY_BONUS_VALUE_UPDATE,
		payload
	};
}

function saveCurrency(payload) {
	return {
		type: SAVE_CURRENCY,
		payload
	};
}

function saveAmount(payload) {
	return {
		type: SAVE_AMOUNT,
		payload
	};
}

function cacheReferrer(payload) {
	return {
		type: CACHE_REFERRER,
		payload
	};
}

function hideMentorMessageDialog() {
	return {
		type: HIDE_MENTOR_MESSAGE_DIALOG
	};
}

function enableMentorMessageDialog() {
	return {
		type: SHOW_MENTOR_MESSAGE_DIALOG
	};
}

function updateMentorMessages(payload) {
	return {
		type: UPDATE_MENTOR_MESSAGES,
		payload
	};
}

function setClosingReason(payload) {
	return {
		type: SET_CLOSING_REASON,
		payload
	};
}

function setReasonGambling(payload) {
	return {
		type: SET_REASON_GAMBLING,
		payload
	};
}

function setFromRG(payload) {
	return {
		type: SET_FROM_RG,
		payload
	};
}

function setOasisReason(payload) {
	return {
		type: SET_OASIS_REASON,
		payload
	};
}

function setShowClosedPopup(payload) {
	return {
		type: SET_SHOW_CLOSED_POPUP,
		payload
	};
}

function setCasinoNetPosition(payload) {
	setCookie(COOKIE_PLAYER_CASINO_NET_POSITION, payload);
	return {
		type: SET_CASINO_NET_POSITION,
		payload
	};
}

function setPlayerID(payload) {
	return {
		type: SET_PLAYER_ID,
		payload
	};
}

function playerSelfExcludedUpdate(payload) {
	return {
		type: PLAYER_SELF_EXCLUDED_UPDATE,
		payload
	};
}

function playerSelfExcludedFailure(payload) {
	return {
		type: PLAYER_SELF_EXCLUDED_FAILURE,
		payload
	};
}

function setShouldShowCrossMarketingConsent(payload) {
	return {
		type: SET_SHOULD_SHOW_CROSS_MARKETING_CONSENT,
		payload
	};
}

function setClosingAccountStatus(payload) {
	return {
		type: SET_CLOSING_ACCOUNT_STATUS,
		payload
	};
}

function setLugasActivationTime(payload) {
	return {
		type: SET_LUGAS_ACTIVATION_TIME,
		payload
	};
}

/** *************************************************************************
 * API
 ************************************************************************** */

const updateGermanyRemainingBonusValue = () => {
	return async dispatch => {
		const { remainingBonusValue } = await getRemainingBonusValue();
		dispatch(remainingGermanyBonusValueUpdate(remainingBonusValue));
	};
};

function request2FASMSCode() {
	return async dispatch => {
		const response = await requestApi(`/player/mfa/sms`, {}, 'POST');
		if (response.status === 'SUCCESS') {
			dispatch({
				type: TWO_FACTOR_AUTH_REQUEST_SMS
			});
		} else {
			if (response.errorCode === 'REGISTRATION_SMS_VERIFICATION_RETRY_COUNT_EXCEEDED') {
				// Specific error for cooloff period
				dispatch(
					showNotification({
						color: 'red',
						autoHide: true,
						messageKey: 'TWO_FACTOR_AUTHENTICATION_SMS_COOLOFF_ERROR',
						openInSidebar: true
					})
				);
			} else {
				// general API error
				dispatch(
					showNotification({
						color: 'red',
						autoHide: true,
						messageKey: 'TWO_FACTOR_AUTHENTICATION_GENERAL_API_ERROR',
						openInSidebar: true
					})
				);
			}

			// eslint-disable-next-line no-console
			console.error(response);
		}
	};
}

function verify2FASMSCode(code) {
	return async dispatch => {
		const response = await requestApi(`/player/mfa/sms/${code}`, {}, 'GET');

		if (response.status === 'SUCCESS') {
			dispatch({
				type: TWO_FACTOR_AUTH_ENABLED
			});
		} else {
			if (response.errorCode === 'REGISTRATION_SMS_VERIFICATION_DURATION_EXPIRED') {
				// error for when sms code has expired
				dispatch(
					showNotification({
						color: 'red',
						autoHide: true,
						messageKey: 'TWO_FACTOR_AUTHENTICATION_CODE_EXPIRED_ERROR',
						openInSidebar: true
					})
				);
			} else if (response.errorCode === 'REGISTRATION_SMS_VERIFICATION_VALIDATION_FAILED') {
				// wrong SMS code error
				dispatch(
					showNotification({
						color: 'red',
						autoHide: true,
						messageKey: 'TWO_FACTOR_AUTHENTICATION_CODE_ERROR',
						openInSidebar: true
					})
				);
			} else {
				dispatch(
					showNotification({
						color: 'red',
						autoHide: true,
						messageKey: 'TWO_FACTOR_AUTHENTICATION_GENERAL_API_ERROR',
						openInSidebar: true
					})
				);
			}

			// eslint-disable-next-line no-console
			console.error(response);
		}
	};
}

function disable2FA() {
	return async dispatch => {
		const response = await requestApi(`/player/mfa/sms`, {}, 'DELETE');

		if (response.status === 'SUCCESS') {
			dispatch({
				type: TWO_FACTOR_AUTH_DISABLED
			});
		} else {
			dispatch(
				showNotification({
					color: 'red',
					autoHide: true,
					messageKey: 'TWO_FACTOR_AUTHENTICATION_DISABLE_ERROR',
					openInSidebar: true
				})
			);
			// eslint-disable-next-line no-console
			console.error(response);
		}
	};
}

function reset2FAStatus() {
	return {
		type: TWO_FACTOR_AUTH_STATUS_RESET
	};
}

function resetSMSSentStatus() {
	return {
		type: TWO_FACTOR_AUTH_REQUEST_SMS_RESET
	};
}

/** *************************************************************************
 * MIDDLEWARES
 ************************************************************************** */
function fetchBalance() {
	return async dispatch => {
		const balance = await getBalance();
		const payload = {
			...balance
		};
		if (!Cookies.get(COOKIE_PLAYER_BALANCE)) {
			setCookie(COOKIE_PLAYER_BALANCE, payload.balance);
		}
		return dispatch({ type: ACCOUNT_SET_BALANCE, payload });
	};
}

function accountHydrate() {
	return async (dispatch, getState) => {
		// FIXME: Aggregate these into one, single call
		const [account, balance, points, playerTotalDeposit, sowfiInfo, playerSelfExcluded] = await Promise.all([
			getAccount(),
			getBalance(),
			getPoints(),
			getPlayerTotalDeposit(),
			getSowfiInfo(),
			getSelfExclusionValue()
		]);

		const {
			account: { useMyActivities, isRegistration },
			brand: {
				isGermanRegulatedFranchise,
				showMentorMessages,
				isLotteriesEnabled,
				isNetherlandsRegulatedFranchise
			}
		} = getState();
		if (isGermanRegulatedFranchise) {
			dispatch(updateGermanyRemainingBonusValue());
		}

		let hasPlayerAtLeastOneFinalizedInterview;
		if (isNetherlandsRegulatedFranchise) {
			hasPlayerAtLeastOneFinalizedInterview = await getPlayerAtLeastOneFinalizedInterviewExists();
		}

		let numOfActiveOffers = {};
		if (useMyActivities) {
			numOfActiveOffers = await getNumOfActiveOffers(isLotteriesEnabled);
		}
		if (showMentorMessages) {
			dispatch({ type: SHOW_MENTOR_MESSAGE_DIALOG });
		}

		const payload = {
			...account,
			...balance,
			...points,
			...playerTotalDeposit,
			...numOfActiveOffers,
			...sowfiInfo,
			...playerSelfExcluded,
			...(hasPlayerAtLeastOneFinalizedInterview ? { hasPlayerAtLeastOneFinalizedInterview } : {}),
			isRegistration: isRegistration ?? account.isRegistration
		};

		if (!Cookies.get(COOKIE_PLAYER_POINTS)) {
			setCookie(COOKIE_PLAYER_POINTS, payload.points);
		}
		if (!Cookies.get(COOKIE_PLAYER_BALANCE)) {
			setCookie(COOKIE_PLAYER_BALANCE, payload.balance);
		}
		return dispatch({ type: ACCOUNT_HYDRATE, payload });
	};
}

function accountGetRisk() {
	return async dispatch => {
		const riskLevels = await getRiskLevels();

		const payload = {
			...riskLevels
		};
		return dispatch({ type: ACCOUNT_GETRISK, payload });
	};
}

function fetchCurrency(forceUpdate = false) {
	return async (dispatch, getState) => {
		const { currency } = getState().account;
		if (!currency || forceUpdate) {
			const payload = await getCurrency();
			return dispatch(accountUpdate(payload));
		}
	};
}

function updateCurrencyLimits(data, oneButtonFlow) {
	return async dispatch => {
		const url = '/PaymentFormDataService/getDepositFeesAndLimits';
		let response;
		let maxDeposit;
		let minDeposit;

		if (oneButtonFlow) {
			const payload = createUrlParameters({
				...data,
				paymentMethodId: 23
			});
			response = await postFormUrlEncoded(`${url}/forMobile`, payload);
			if (response.RESULT === 'OK') {
				response.status = 'SUCCESS';
				minDeposit = response.depositLimitData?.PARAM_MIN_OPERATION || 0;
				maxDeposit = response.depositLimitData?.PARAM_MAX_OPERATION || 9999;
			}
		} else {
			response = await postJson(`${url}/feesAndLimitsForNotLoggedInPlayer`, data);
			if (response.status === 'SUCCESS') {
				minDeposit = response.result.minDeposit.toFixed(2);
				maxDeposit = response.result.maxDeposit.toFixed(2);
			}
		}

		if (response.status === 'SUCCESS') {
			dispatch({
				type: CURRENCY_LIMITS,
				payload: {
					min: parseFloat(minDeposit),
					max: parseFloat(maxDeposit)
				}
			});
		} else {
			// eslint-disable-next-line no-console
			console.error(response);
		}
	};
}

const displayDepositResultScreen = () => async dispatch => {
	dispatch({
		type: DEPOSIT_RESULT_PAGE_OPEN,
		payload: true
	});
};

const updateSelfExclusionValue = () => async (dispatch, getState) => {
	const isUserSelfExcluded = getState().account.playerSelfExcluded;
	// This is a toggle value update, which we always want to update to the oposite value of what it was
	const updatedValue = !isUserSelfExcluded;
	const payload = { blocked: updatedValue };
	try {
		const response = await requestApi('/player/bonusExclusion', payload, 'PUT');
		if (response.status === 'SUCCESS') {
			dispatch(playerSelfExcludedUpdate(updatedValue));
		}
	} catch (err) {
		dispatch(playerSelfExcludedFailure(true));
		// eslint-disable-next-line no-console
		console.error(err);
	}
};

export const updateLimitsModal =
	(open = false, data = {}, informMobileApps = true) =>
	(dispatch, getState) => {
		const isModalOpen = getState().account.limitsModal?.open;
		if (informMobileApps && isModalOpen && !open) {
			sendAppBridgeEvent(WEB_EVENTS.LIMIT_MODAL_CLOSED);
		}
		dispatch({
			type: UPDATE_LIMITS_MODAL,
			payload: { open, data }
		});
	};

export const updateSuspendedPlayerModal =
	(open = false, data = { operationType: null }, informMobileApps = true) =>
	(dispatch, getState) => {
		const isModalOpen = getState().account.suspendedPlayerModal?.open;

		// If modal is closing and we need to inform mobile apps
		if (informMobileApps && isModalOpen && !open) {
			sendAppBridgeEvent(WEB_EVENTS.SUSPENDED_PLAYER_MODAL_CLOSED);
		}

		dispatch({
			type: UPDATE_SUSPENDED_PLAYER_MODAL,
			payload: {
				open,
				data: {
					...data,
					// Make sure operationType is either 'DEPOSIT' or 'WITHDRAWAL'
					operationType: [OPERATION_TYPES.DEPOSIT, OPERATION_TYPES.WITHDRAW].includes(data.operationType)
						? data.operationType
						: null
				}
			}
		});
	};

const updateVerificationMobileStatus = payload => ({
	type: UPDATE_VERIFICATION_MOBILE_STATUS,
	payload
});
/** *************************************************************************
 * REDUCER
 ************************************************************************** */

const initialState = {
	depositResultPageOpen: false,
	casinoNetPosition: '0'
	// initial value for failure to false
};

function reducer(state = initialState, { type, payload }) {
	switch (type) {
		case SET_CLOSING_REASON:
			return { ...state, closingReason: payload };
		case SET_REASON_GAMBLING:
			return { ...state, isReasonGambling: payload };
		case SET_FROM_RG:
			return { ...state, isFromRG: payload };
		case SET_OASIS_REASON:
			return { ...state, oasisReason: payload };
		case SET_SHOW_CLOSED_POPUP:
			return { ...state, showClosedPopup: payload };
		case SAVE_AMOUNT:
			return { ...state, savedAmount: payload };
		case SAVE_CURRENCY:
			return { ...state, currency: payload };
		case CURRENCY_LIMITS:
			return { ...state, depositLimits: payload };
		case SET_CLOSING_ACCOUNT_STATUS:
			return {
				...state,
				isClosingAccount: payload
			};
		case SET_LUGAS_ACTIVATION_TIME:
			return {
				...state,
				lugasActivationTime: payload
			};
		case LOGOUT:
			return {
				useMyActivities: state.useMyActivities,
				currency: state.currency,
				locale: state.locale,
				from: state.from,
				exitPath: state.exitPath,
				showClosedPopup: state.showClosedPopup,
				hydrated: false
			};
		case LOGIN_START:
			return {
				...state,
				isRegistration: false
			};
		case ACCOUNT_HYDRATE:
			return {
				...state,
				...payload,
				hydrated: true,
				// playerSelfExcluded: false, // this should be deleted once the value is fetched from BE (line 433)
				playerSelfExcludedFailure: false
			};
		case ACCOUNT_SET_BALANCE:
			return {
				...state,
				...payload
			};
		case ACCOUNT_VERIFICATIONS_UPDATE:
			return {
				...state,
				playerVerificationsVO: {
					...state.playerVerificationsVO,
					...payload
				}
			};
		case REMAINING_GERMANY_BONUS_VALUE_UPDATE:
			return {
				...state,
				remainingGermanyBonusValue: payload
			};
		case ACCOUNT_UPDATE:
		case CACHE_REFERRER:
		case ACCOUNT_GETRISK:
		case AUTHENTICATION_COMPLETE: // <-- why do we pollute account with authentication stuff? auth_compelete has 'from' property, which is used by auth-middleware
			return { ...state, ...payload };
		case SET_CASINO_NET_POSITION:
			return { ...state, casinoNetPosition: payload };
		case UPDATE_VERIFICATION_MOBILE_STATUS: {
			return {
				...state,
				verificationMessageSent: payload
			};
		}
		case SHOW_MENTOR_MESSAGE_DIALOG:
			return {
				...state,
				showMentorMessageDialog: true
			};
		case HIDE_MENTOR_MESSAGE_DIALOG:
			return {
				...state,
				showMentorMessageDialog: false
			};
		case UPDATE_MENTOR_MESSAGES:
			return {
				...state,
				mentorMessages: payload
			};
		case DEPOSIT_RESULT_PAGE_OPEN: {
			return {
				...state,
				depositResultPageOpen: payload
			};
		}
		case TWO_FACTOR_AUTH_REQUEST_SMS: {
			return {
				...state,
				twoFactorAuthenticationSMSSent: true
			};
		}
		case TWO_FACTOR_AUTH_REQUEST_SMS_RESET: {
			return {
				...state,
				twoFactorAuthenticationSMSSent: false
			};
		}
		case TWO_FACTOR_AUTH_ENABLED: {
			return {
				...state,
				twoFactorAuthenticationEnabled: true
			};
		}
		case TWO_FACTOR_AUTH_DISABLED: {
			return {
				...state,
				twoFactorAuthenticationDisabled: true
			};
		}
		case TWO_FACTOR_AUTH_STATUS_RESET: {
			return {
				...state,
				twoFactorAuthenticationEnabled: false,
				twoFactorAuthenticationDisabled: false
			};
		}
		case SET_NUM_OF_ACTIVE_OFFERS: {
			return {
				...state,
				numOfActiveOffers: payload
			};
		}
		case SET_PLAYER_ID: {
			return {
				...state,
				id: payload
			};
		}
		case PLAYER_SELF_EXCLUDED_UPDATE: {
			return {
				...state,
				playerSelfExcluded: payload
			};
		}
		case PLAYER_SELF_EXCLUDED_FAILURE: {
			return {
				...state,
				playerSelfExcludedFailure: payload
			};
		}
		case SET_SHOULD_SHOW_CROSS_MARKETING_CONSENT:
			return { ...state, shouldShowCrossMarketingConsent: payload };

		case UPDATE_LIMITS_MODAL:
			return {
				...state,
				limitsModal: payload
			};

		case UPDATE_SUSPENDED_PLAYER_MODAL:
			return {
				...state,
				suspendedPlayerModal: payload
			};

		default:
			return state;
	}
}

reducerRegistry.register(REDUCER_NAME, reducer);
export {
	selectAccount,
	selectIsCasinoAndSportsBookProduct,
	accountHydrate,
	playerVerificationsUpdate,
	accountUpdate,
	fetchCurrency,
	fetchBalance,
	cacheReferrer,
	updateCurrencyLimits,
	accountGetRisk,
	selectBalance,
	selectFormattedBalance,
	selectCurrency,
	selectLocale,
	selectFranchiseId,
	selectTestPlayer,
	selectPlayerId,
	selectCountryId,
	selectPoints,
	selectHydrated,
	selectExitPath,
	selectMentorMessages,
	selectPlayerFullName,
	selectInvertedPlayerFullName,
	selectIsReasonGambling,
	selectIsFromRG,
	selectClosingReason,
	selectOasisReason,
	selectShowClosedPopup,
	selectLimitsData,
	selectNumOfActiveOffers,
	selectDateOfBirth,
	selectClosureDetails,
	selectIsAboveAgeLimit,
	selectSportsbookBalance,
	selectHasAutofilledAddress,
	selectIntRef,
	selectAllBalances,
	UPDATE_VERIFICATION_MOBILE_STATUS,
	ACCOUNT_UPDATE,
	SAVE_CURRENCY,
	SAVE_AMOUNT,
	CACHE_REFERRER,
	CURRENCY_LIMITS,
	ACCOUNT_GETRISK,
	ACCOUNT_HYDRATE,
	SET_NUM_OF_ACTIVE_OFFERS,
	REMAINING_GERMANY_BONUS_VALUE_UPDATE,
	setClosingReason,
	setReasonGambling,
	setFromRG,
	setOasisReason,
	setShowClosedPopup,
	saveAmount,
	saveCurrency,
	accountDataSelector,
	enableMentorMessageDialog,
	hideMentorMessageDialog,
	updateMentorMessages,
	UPDATE_MENTOR_MESSAGES,
	displayDepositResultScreen,
	selectAutoWithdraw,
	selectShowMentorMessageDialog,
	selectIsRegistration,
	request2FASMSCode,
	verify2FASMSCode,
	disable2FA,
	twoFactorAuthDisabled,
	twoFactorAuthEnabled,
	reset2FAStatus,
	resetSMSSentStatus,
	setCasinoNetPosition,
	selectCasinoNetPosition,
	selectUsername,
	selectPlayerVerifications,
	setPlayerID,
	selectRemainingGermanyBonusValue,
	updateGermanyRemainingBonusValue,
	selectPlayerSelfExcluded,
	updateSelfExclusionValue,
	selectPlayerSelfExcludedFailure,
	selectPlayerBonusBlocked,
	setShouldShowCrossMarketingConsent,
	selectShouldShowSplashtech,
	updateVerificationMobileStatus,
	selectPlayerToken,
	setClosingAccountStatus,
	setLugasActivationTime
};
export default reducer;
